import {
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid2 as Grid,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo } from "react";
import PageFilter from "../../../../Components/Misc/PageFilter";

import { HourglassDisabledOutlined, HourglassEmptyOutlined, SkipNextOutlined } from "@mui/icons-material";
import PlayCircleOutlineOutlinedIcon from "@mui/icons-material/PlayCircleOutlineOutlined";
import ThumbDownOutlinedIcon from "@mui/icons-material/ThumbDownOutlined";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import { GridColDef, GridRowParams, GridRowsProp, GridSortModel } from "@mui/x-data-grid-premium";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router";
import { ModalContext } from "../../../../Components/Modals";
import ModalApprovalAction, {
  ModalApprovalActionElementProps,
} from "../../../../Components/Modals/ModalApprovalAction/modal-approval-action";
import AssetPanel from "../../../../Components/Panels/AssetPanel/asset-panel";
import DataTable, { CellSize } from "../../../../Components/Table/table";
import TableCellApprovalRequest from "../../../../Components/Table/TableCellApprovalRequest";
import TableCellAsset from "../../../../Components/Table/TableCellAsset/table-cell-asset";
import TableCellDateTime from "../../../../Components/Table/TableCellDateTime/table-cell-date-time";
import TableCellExpiresIn from "../../../../Components/Table/TableCellExpiresIn/table-cell-expires-in";
import TableCellJobStatus from "../../../../Components/Table/TableCellJobStatus/table-cell-job-status";
import TableCellName from "../../../../Components/Table/TableCellName";
import { EventNotifierOperation } from "../../../../Core/Api/notifier-client";
import { cacheService, eventsNotifier, userService } from "../../../../Core/Api/services";
import { DbUserAclElevationWishlistInfos } from "../../../../Core/Api/Types/db-user-acl-elevation-wishlist-infos";
import { ObjectType } from "../../../../Core/Api/Types/object-type";
import { OperationRequestStatus } from "../../../../Core/Api/Types/operation-request-status";
import { OperationRequestType } from "../../../../Core/Api/Types/operation-request-type";
import { WorkflowStatus } from "../../../../Core/Api/Types/workflow-status";
import { getExpirationDaysFromString } from "../../../../Utils/helpers";
import { useToggle } from "../../../../Utils/Hooks/useToggle";
import TableUuids from "../../../../Components/Table/table-uuids";

function DashboardApprovals() {
  const { i18n, t } = useTranslation();
  const [searchFilter, setSearchFilter] = React.useState<string>("");
  const [tableSorting, setTableSorting] = React.useState<GridSortModel>([]);
  const [approvals, setApprovals] = React.useState<DbUserAclElevationWishlistInfos[]>([]);
  const [approvalStatus, setApprovalStatus] = React.useState<OperationRequestStatus | null>(
    OperationRequestStatus.READY
  );
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [showOnlyMyActivities, setShowOnlyMyActivities] = React.useState<boolean>(false);
  const [assetSelected, setAssetSelected] = React.useState<number>();
  const [rowSelected, setRowSelected] = React.useState<number>();
  const myUserId = React.useMemo(() => cacheService.GetUserInfo()!.id, []);
  const { active: assetPanelOpened, toggle: toggleAssetPanelOpen } = useToggle(false);

  const modalContext = React.useContext(ModalContext);
  const { openModal } = { ...modalContext };

  // Outlet context used to pass props to parent DashboardLayout through react router dom
  const { selectedApprovals, setSelectedApprovals } = useOutletContext<{
    setSelectedApprovals: React.Dispatch<React.SetStateAction<DbUserAclElevationWishlistInfos[]>>;
    selectedApprovals: DbUserAclElevationWishlistInfos[];
  }>();

  const handleOpenModalAction = React.useCallback(
    (operation: "Approve" | "Decline" | "ApproveAndRun" | "Run", approval?: DbUserAclElevationWishlistInfos) => {
      if (!openModal) return;
      const onClose: ModalApprovalActionElementProps["onClose"] = (data) => {};

      openModal<ModalApprovalActionElementProps>(ModalApprovalAction, {
        onClose,
        operation: operation,
        approvals: approval ? [approval] : selectedApprovals,
      });
    },
    [openModal, selectedApprovals]
  );

  React.useEffect(() => {
    userService.GetPagedWishlist(0, 600).then(function (response) {
      if (response.success && response.operations) setApprovals(response.operations);
      setIsLoading(false);
    });
  }, []);

  React.useEffect(() => {
    if (assetPanelOpened) toggleAssetPanelOpen();
  }, [approvalStatus]);

  const approvalsRows: GridRowsProp = approvals;
  type Row = (typeof approvalsRows)[number];

  const approvalsColumns: GridColDef<DbUserAclElevationWishlistInfos>[] = [
    {
      field: "titleName",
      headerName: t("common:assetName"),
      minWidth: CellSize.xlarge,
      flex: CellSize.xlarge,
      valueGetter: (data, row) => `${row.titleName} - ${row?.requestInfos?.objectName}`,
      renderCell: (data) => (
        <TableCellAsset
          data={{
            row: {
              desc: data.row.requestInfos?.objectName ?? "",
              title: data.row.titleName ?? "",
              type: ObjectType.Folder,
              typeHide: true,
            },
          }}
        />
      ),
    },
    {
      field: "type",
      headerName: t("common:request"),
      minWidth: CellSize.large,
      flex: CellSize.large,
      type: "singleSelect",
      valueOptions: Object.keys(OperationRequestType),
      renderCell: (data) => (
        <TableCellApprovalRequest
          data={{ value: { type: data.row.type!, description: data.row.annotation ?? "" } }}
        ></TableCellApprovalRequest>
      ),
    },
    {
      field: "creationInfos",
      headerName: t("common:from"),
      type: "singleSelect",
      valueGetter: (data, row) => row?.creationInfos?.userName,
      valueOptions: approvals.reduce(
        (acc: string[], x) =>
          !x.creationInfos?.userName || acc.includes(x.creationInfos?.userName)
            ? acc
            : [...acc, x.creationInfos!.userName!],
        [] as string[]
      ),
      minWidth: CellSize.medium,
      flex: CellSize.medium,
      renderCell: (data) => (
        <TableCellName
          data={{
            value: {
              firstname: data?.row?.creationInfos?.userName
                ? data?.row?.creationInfos.userName.split(" ")[0] || ""
                : "",
              lastname: data?.row?.creationInfos?.userName ? data?.row?.creationInfos.userName.split(" ")[1] || "" : "",
              id: data?.row?.creationInfos?.userId ?? 0,
            },
          }}
        />
      ),
    },
    {
      field: "date",
      headerName: t("common:createdAt"),
      minWidth: CellSize.small,
      flex: CellSize.small,
      type: "date",
      valueGetter: (data, row) => (row?.creationInfos?.date ? new Date(row?.creationInfos?.date) : null),
      renderCell: (data) => <TableCellDateTime data={{ value: { date: data.value } }} />,
    },
    {
      field: "reviewerUserName",
      headerName: t("common:reviewer"),
      minWidth: CellSize.medium,
      flex: CellSize.medium,
      valueOptions: approvals.reduce(
        (acc: string[], x) =>
          !x.reviewerUserName || acc.includes(x.reviewerUserName) ? acc : [...acc, x.reviewerUserName],
        [] as string[]
      ),
      renderCell: (data) => {
        if (data.row.status === OperationRequestStatus.SUPERSEDED) {
          return (
            <Stack direction="row" spacing={1.5} maxWidth={"100%"} alignItems="center">
              <SkipNextOutlined color="disabled" />
              <Tooltip title={"Action triggered by another user with the necessay permissions"} placement="right">
                <Typography color="text.secondary">{t("common:superseded")}</Typography>
              </Tooltip>
            </Stack>
          );
        }

        return (
          <TableCellName
            data={{
              value: {
                firstname: data.row.reviewerUserName ? data.row.reviewerUserName.split(" ")[0] || "" : "",
                lastname: data.row.reviewerUserName ? data.row.reviewerUserName.split(" ")[1] || "" : "",
                id: data.row.reviewerUserId ?? 0,
              },
            }}
          />
        );
      },
    },
    {
      field: "reviewedAt",
      headerName: t("table:headerName.reviewedOn"),
      minWidth: CellSize.small,
      flex: CellSize.small,
      type: "date",
      valueGetter: (data, row) =>
        row?.status === OperationRequestStatus.SUPERSEDED || !row.reviewedAt ? null : new Date(row.reviewedAt),
      renderCell: (data) => <TableCellDateTime data={{ value: { date: data.value } }} />,
    },
    {
      field: "runnedByUserName",
      headerName: t("table:headerName.runnedBy"),
      minWidth: CellSize.medium,
      flex: CellSize.medium,
      valueOptions: approvals.reduce(
        (acc: string[], x) =>
          !x.runnedByUserName || acc.includes(x.runnedByUserName) ? acc : [...acc, x.runnedByUserName],
        [] as string[]
      ),
      renderCell: (data) => (
        <TableCellName
          data={{
            value: {
              firstname: data?.row?.runnedByUserName ? data?.row?.runnedByUserName.split(" ")[0] || "" : "",
              lastname: data?.row?.runnedByUserName ? data?.row?.runnedByUserName.split(" ")[1] || "" : "",
              id: data?.row?.runnedByUserId ?? 0,
            },
          }}
        />
      ),
    },
    {
      field: "runnedAt",
      headerName: t("table:headerName.ranOn"),
      minWidth: CellSize.small,
      flex: CellSize.small,
      type: "date",
      valueGetter: (data, row) => (row.runnedAt ? new Date(row.runnedAt) : null),
      renderCell: (data) => <TableCellDateTime data={{ value: { date: data.value } }} />,
    },

    {
      field: "approvalExpirationDate",
      headerName: t("table:headerName.expiresIn"),
      type: "number",
      headerAlign: "left",
      align: "left",
      valueGetter: (params, row) => (params ? getExpirationDaysFromString(params) : null),
      renderCell: (params) => (params.value ? <TableCellExpiresIn daysNumber={params.value} /> : null),
      minWidth: CellSize.small,
      flex: CellSize.small,
    },
    {
      field: "workflowStatus",
      headerName: t("common:workflowStatus"),
      minWidth: CellSize.medium,
      flex: CellSize.medium,
      type: "singleSelect",
      valueOptions: Object.keys(WorkflowStatus),
      renderCell: (data) => {
        if (data.value) {
          return <TableCellJobStatus data={data.value} />;
        } else {
          return <></>;
        }
      },
    },
    {
      field: "actions",
      headerName: "",
      type: "actions",
      align: "right",
      minWidth: approvalStatus === OperationRequestStatus.READY ? CellSize.actionsSmall : CellSize.actionsXsmall,
      flex: approvalStatus === OperationRequestStatus.READY ? CellSize.actionsSmall : CellSize.actionsXsmall,
      getActions: (params: GridRowParams) => {
        if (!params || !params.row) return [];
        const buttonApprove = (
          <Tooltip title={t("tooltip:title.approveOnly")} arrow>
            <span>
              <Button
                color="success"
                onClick={() =>
                  handleOpenModalAction(
                    "Approve",
                    approvals.find((x) => x.id === params.row["id"])
                  )
                }
                disabled={params.row["isExpired"]}
                size="small"
                sx={{ mr: 1 }}
              >
                <ThumbUpOutlinedIcon color="success" />
              </Button>
            </span>
          </Tooltip>
        );

        const buttonDecline = (
          <Tooltip title={t("common:decline")} arrow>
            <span>
              <Button
                color="error"
                onClick={() =>
                  handleOpenModalAction(
                    "Decline",
                    approvals.find((x) => x.id === params.row["id"])
                  )
                }
                disabled={params.row["isExpired"]}
                size="small"
                sx={{ mr: 1 }}
              >
                <ThumbDownOutlinedIcon color="error" />
              </Button>
            </span>
          </Tooltip>
        );

        const buttonApproveAndRun = (
          <Tooltip title={t("tooltip:title.approveAndRunTask")} arrow>
            <span>
              <Button
                onClick={() =>
                  handleOpenModalAction(
                    "ApproveAndRun",
                    approvals.find((x) => x.id === params.row["id"])
                  )
                }
                disabled={params.row["isExpired"]}
                size="small"
              >
                <PlayCircleOutlineOutlinedIcon />
              </Button>
            </span>
          </Tooltip>
        );

        const buttonRun = (
          <Tooltip title={t("tooltip:title.runTask")} arrow>
            <span>
              <Button
                onClick={() =>
                  handleOpenModalAction(
                    "Run",
                    approvals.find((x) => x.id === params.row["id"])
                  )
                }
                disabled={params.row["isExpired"]}
                size="small"
              >
                <PlayCircleOutlineOutlinedIcon />
              </Button>
            </span>
          </Tooltip>
        );

        if (params.row["status"] === OperationRequestStatus.READY)
          return [buttonApprove, buttonDecline, buttonApproveAndRun];
        else if (params.row["status"] === OperationRequestStatus.APPROVED) return [buttonRun];
        else return [];
      },
    },
  ];

  const openAssetPanel = (row) => {
    setAssetSelected(row.row.requestInfos.objectId);
    setRowSelected(row.row.id);
    if (!assetPanelOpened) toggleAssetPanelOpen();
  };

  const filteredApprovals = useMemo(() => {
    const output = approvalsRows.filter(
      (row) =>
        (searchFilter === "" ||
          row.titleName?.includesAllWords(searchFilter) ||
          row.creationInfos.userName?.includesAllWords(searchFilter) ||
          row.annotation?.includesAllWords(searchFilter) ||
          row.requestInfos.objectId.toString().includes(searchFilter)) &&
        (showOnlyMyActivities ? row.creationInfos.userId === myUserId : true)
    );

    switch (approvalStatus) {
      case OperationRequestStatus.READY:
        return output.filter((x) => x.status === OperationRequestStatus.READY && !x.isExpired);
      case OperationRequestStatus.APPROVED:
        return output.filter((x) => x.status === OperationRequestStatus.APPROVED && !x.isExpired);
      case OperationRequestStatus.EXECUTED:
        return output.filter(
          (x) => x.status === OperationRequestStatus.EXECUTED || x.status === OperationRequestStatus.SUPERSEDED
        );
      case OperationRequestStatus.DECLINED:
        return output.filter((x) => x.status === OperationRequestStatus.DECLINED);
      case null:
        return output.filter(
          (x) =>
            (x.status === OperationRequestStatus.READY || x.status === OperationRequestStatus.APPROVED) && x.isExpired
        );
      default:
        return output;
    }
  }, [approvalsRows, searchFilter, showOnlyMyActivities, myUserId, approvalStatus]);

  useEffect(() => {
    // isExpired or READY
    switch (approvalStatus) {
      case null:
      case OperationRequestStatus.READY:
        setTableSorting([{ field: "date", sort: "desc" }]);
        break;
      case OperationRequestStatus.APPROVED:
      case OperationRequestStatus.DECLINED:
        setTableSorting([{ field: "reviewedAt", sort: "desc" }]);
        break;
      case OperationRequestStatus.EXECUTED:
        setTableSorting([{ field: "runnedAt", sort: "desc" }]);
        break;
    }
  }, [approvalStatus]);

  useEffect(() => {
    eventsNotifier.Connect().then(function (result) {
      console.debug("Hub events connected successfully to WebSocket and is listening to UserAclElevationMessage!");
      if (eventsNotifier.connection)
        eventsNotifier.connection.on("UserAclElevationMessage", (data) => {
          switch (data.op) {
            case EventNotifierOperation.Create:
              console.debug("UserAclElevationMessage - Create", data);
              setApprovals((prev) => [...prev, data.data]);
              break;
            case EventNotifierOperation.Update:
              console.debug("UserAclElevationMessage - Update", data);
              setApprovals((prev) => prev.map((x) => (x.id === data.data.id ? data.data : x)));
              break;
            case EventNotifierOperation.Delete:
              console.debug("UserAclElevationMessage - Delete", data);
              setApprovals((prev) => prev.filter((x) => x.id !== data.data.id));
              break;
          }
        });
    });

    return () => {
      eventsNotifier.Disconnect();
    };
  }, []);
  const xsValue = 12 / 5;

  const additionalFilters = (
    <FormGroup>
      <FormControlLabel
        sx={{ ml: 1, mr: 1 }}
        control={<Checkbox onChange={(e) => setShowOnlyMyActivities(e.target.checked)} />}
        label={t("field:label.showOnlyMine")}
      />
    </FormGroup>
  );
  return (
    <>
      <Grid container spacing={1} sx={{ py: 2 }}>
        <Grid size={xsValue}>
          <PageFilter
            active={approvalStatus === OperationRequestStatus.READY}
            onClick={() => setApprovalStatus(OperationRequestStatus.READY)}
            small
            variant="primary"
            title={approvals.reduce(
              (acc, x) => (x.status === OperationRequestStatus.READY && !x.isExpired ? acc + 1 : acc),
              0
            )}
            icon={<HourglassEmptyOutlined />}
            subtitle={t("page:pageFilter.subTitle.pendingApproval")}
          />
        </Grid>
        <Grid size={xsValue}>
          <PageFilter
            active={approvalStatus === OperationRequestStatus.APPROVED}
            onClick={() => setApprovalStatus(OperationRequestStatus.APPROVED)}
            small
            variant="success"
            title={approvals.reduce(
              (acc, x) => (x.status === OperationRequestStatus.APPROVED && !x.isExpired ? acc + 1 : acc),
              0
            )}
            subtitle={t("common:approved")}
            icon={<ThumbUpOutlinedIcon />}
          />
        </Grid>
        <Grid size={xsValue}>
          <PageFilter
            active={approvalStatus === OperationRequestStatus.EXECUTED}
            onClick={() => setApprovalStatus(OperationRequestStatus.EXECUTED)}
            small
            variant="success"
            title={approvals.reduce(
              (acc, x) =>
                x.status === OperationRequestStatus.EXECUTED || x.status === OperationRequestStatus.SUPERSEDED
                  ? acc + 1
                  : acc,
              0
            )}
            subtitle={t("common:executed")}
            icon={<PlayCircleOutlineOutlinedIcon />}
          />
        </Grid>
        <Grid size={xsValue}>
          <PageFilter
            active={approvalStatus === OperationRequestStatus.DECLINED}
            onClick={() => setApprovalStatus(OperationRequestStatus.DECLINED)}
            small
            variant="error"
            title={approvals.reduce((acc, x) => (x.status === OperationRequestStatus.DECLINED ? acc + 1 : acc), 0)}
            subtitle={t("common:declined")}
            icon={<ThumbDownOutlinedIcon />}
          />
        </Grid>
        <Grid size={xsValue}>
          <PageFilter
            active={approvalStatus === null} // Expired
            onClick={() => setApprovalStatus(null)}
            small
            variant="warning"
            title={approvals.reduce(
              (acc, x) =>
                (x.status === OperationRequestStatus.READY || x.status === OperationRequestStatus.APPROVED) &&
                x.isExpired
                  ? acc + 1
                  : acc,
              0
            )}
            subtitle={t("common:expired")}
            icon={<HourglassDisabledOutlined />}
          />
        </Grid>
      </Grid>
      <DataTable
        loading={isLoading}
        tableKey={TableUuids.DASHBOARD_APPROVALS_TABLE}
        sortModel={tableSorting}
        onSortModelChange={(newModelSorting) => setTableSorting(newModelSorting)}
        rows={filteredApprovals}
        initialState={{ pinnedColumns: { left: ["__check__"], right: ["actions"] } }}
        overrideColumnVisibility={{
          reviewerUserName: approvalStatus !== OperationRequestStatus.READY,
          reviewedAt: approvalStatus !== OperationRequestStatus.READY,
          actions:
            approvalStatus === OperationRequestStatus.READY || approvalStatus === OperationRequestStatus.APPROVED,
          __check__:
            approvalStatus === OperationRequestStatus.READY || approvalStatus === OperationRequestStatus.APPROVED,
          runnedByUserName: approvalStatus === OperationRequestStatus.EXECUTED,
          runnedAt: approvalStatus === OperationRequestStatus.EXECUTED,
          workflowStatus: approvalStatus === OperationRequestStatus.EXECUTED,
          approvalExpirationDate: approvalStatus === OperationRequestStatus.APPROVED,
        }}
        columns={approvalsColumns}
        additionalFilters={additionalFilters}
        getRowHeight={() => "auto"}
        onRowClick={(e) => openAssetPanel(e)}
        disableRowSelectionOnClick
        checkboxSelection
        selectedRows={approvals.filter((x) => selectedApprovals.map((s) => s.id).includes(x.id)).map((s) => s.id)}
        setSelectedRows={(v) => setSelectedApprovals(approvals.filter((x) => (v as number[]).includes(x.id)))}
      />
      <AssetPanel
        open={assetPanelOpened}
        onClose={toggleAssetPanelOpen}
        closePanel={toggleAssetPanelOpen}
        assetId={assetSelected}
        rowId={rowSelected}
      />
    </>
  );
}

export default DashboardApprovals;
